<template>
    <div v-if="org">
        <OrgView :is-org-member="isOrgMember" :opportunities="data?.opportunities" :org="org" />
        <!-- <RecommendedItems :id="org.id" collection="orgs" :keys="recommendedKeys" :related-items="relatedItems" /> -->
    </div>
</template>

<script setup lang="ts">
import type { Job } from '#shared/types/Job'
import type { Org } from '#shared/types/Org'
import { OrgType } from '#shared/types/Org'
import type { Project } from '#shared/types/Project'
import type { Skill } from '#shared/types/Skill'
import { VisibilityType } from '#shared/types/visibility'
import { getOrgSocialLinks, orgDescriptionToPlainText } from '#shared/utils/orgProfile'
import { isPublicVolunteeringWeekDraftProject } from '#shared/utils/publicProjectFilters'
import { storeToRefs } from 'pinia'
import { useOrgs } from '@/composables/orgs/useOrgs'
import { useSharing } from '@/composables/sharing/useSharing'

type OrgPageData = {
    org: Org
    canonicalSlug: string
    thin: boolean
    opportunities: {
        jobs: Job[]
        skills: Skill[]
        projects: Project[]
    }
}

definePageMeta({
    layout: 'default'
})

const SITE_URL = 'https://growni.sk'
const META_DESCRIPTION_MAX_LENGTH = 155
// A description shorter than this says less than the generated fallback
const META_DESCRIPTION_MIN_LENGTH = 50

const ORG_SCHEMA_TYPES: Partial<Record<OrgType, string>> = {
    [OrgType.nonProfit]: 'NGO',
    [OrgType.publicSector]: 'GovernmentOrganization',
    [OrgType.school]: 'EducationalOrganization'
}

const { getSharingUrl } = useSharing()
const { loadUserOrgs, canEditOrgOpportunity, isOrgFieldVisible, getOrgFieldFunction } = useOrgs()

const route = useRoute()
const applicationStore = useApplicationStore()
const { userId } = storeToRefs(applicationStore)

const orgParam = route.params.id as string

// Public profile and opportunities rendered on the server. Unknown and deleted orgs must
// answer 404 — the page used to load in onMounted, so every /orgs/* URL was the same empty 200 shell.
const { data, error } = await useFetch<OrgPageData>(`/api/orgs/${encodeURIComponent(orgParam)}`, {
    key: `org-${orgParam}`
})

if (error.value || !data.value) {
    const statusCode = (error.value?.statusCode ?? 404) >= 500 ? 500 : 404
    throw createError({
        statusCode,
        statusMessage: statusCode === 404 ? 'Organizácia sa nenašla' : 'Organizáciu sa nepodarilo načítať',
        fatal: true
    })
}

// The server sends only public fields (no memberIds). After hydration a signed-in user's own
// orgs (Firestore `memberIds array-contains uid`) tell whether they belong to this one; a member
// then gets the full document, which switches on the member tools (OrgMembers, edit buttons).
const memberOrg = ref<Org>()
const org = computed(() => memberOrg.value || data.value?.org)
const isOrgMember = computed(() => canEditOrgOpportunity(userId.value, memberOrg.value))

async function loadMemberOrg() {
    const orgId = data.value?.org.id
    if (!userId.value || !orgId) {
        memberOrg.value = undefined
        return
    }

    try {
        const userOrgs = await loadUserOrgs()
        memberOrg.value = userOrgs.find((userOrg) => userOrg.id === orgId)
    } catch (err) {
        console.error('Error loading org for member check:', err)
    }
}

onMounted(loadMemberOrg)

// Signing in on the page (auth modal) should reveal the member tools too
watch(userId, loadMemberOrg)

// SEO
const orgName = computed(() => org.value?.title?.trim() || '')
const pageTitle = computed(() => `${orgName.value} | GROWNi.sk`)
const canonicalUrl = computed(() => `${SITE_URL}/orgs/${encodeURIComponent(data.value?.canonicalSlug || orgParam)}`)

// Public-sector profiles show some fields only on a paid plan; the meta tags follow the page
function visibleField<K extends 'desc' | 'image' | 'web' | 'social' | 'address'>(field: K): Org[K] | undefined {
    const current = org.value
    if (!current?.[field] || !isOrgFieldVisible(current, field)) return undefined
    return getOrgFieldFunction(current, field)(current[field])
}

function truncate(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text
    const cut = text.slice(0, maxLength - 1)
    const lastSpace = cut.lastIndexOf(' ')
    return `${(lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,.;:–-]+$/, '')}…`
}

// Accepts "https://…", "www.example.sk" or "example.sk/page"; anything else is not a link
function toAbsoluteUrl(value: unknown): string | undefined {
    if (typeof value !== 'string' || !value.trim()) return undefined
    const trimmed = value.trim()
    const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`

    try {
        const url = new URL(candidate)
        return url.hostname.includes('.') ? url.href : undefined
    } catch {
        return undefined
    }
}

function countPublic(items: (Job | Skill | Project)[] = []): number {
    return items.filter(
        (item) => item.visibility === VisibilityType.published || isPublicVolunteeringWeekDraftProject(item)
    ).length
}

const plainDescription = computed(() => orgDescriptionToPlainText(visibleField('desc')))

const fallbackDescription = computed(() => {
    const opportunities = data.value?.opportunities
    const counts = [
        { count: countPublic(opportunities?.jobs), label: 'pracovné ponuky' },
        { count: countPublic(opportunities?.projects), label: 'dobrovoľnícke projekty' },
        { count: countPublic(opportunities?.skills), label: 'vzdelávacie príležitosti' }
    ].filter(({ count }) => count > 0)

    if (!counts.length) {
        return `${orgName.value} na GROWNi.sk – profil organizácie, jej pracovné ponuky, stáže a dobrovoľnícke projekty. Pozri si, čo ponúka, a zapoj sa.`
    }

    const summary = counts.map(({ count, label }) => `${label} (${count})`).join(', ')
    return `${orgName.value} na GROWNi.sk – aktuálne ${summary}. Pozri si profil organizácie a zapoj sa.`
})

const metaDescription = computed(() => {
    const description =
        plainDescription.value.length >= META_DESCRIPTION_MIN_LENGTH ? plainDescription.value : fallbackDescription.value
    return truncate(description, META_DESCRIPTION_MAX_LENGTH)
})

const logoUrl = computed(() => toAbsoluteUrl(visibleField('image')))
const ogImage = computed(() => getSharingUrl('org', visibleField('image') || ''))

const organizationJsonLd = computed(() => {
    const current = org.value
    if (!current) return null

    const socialUrls = isOrgFieldVisible(current, 'social') ? getOrgSocialLinks(current).map(({ url }) => url) : []
    const sameAs = [toAbsoluteUrl(visibleField('web')), ...socialUrls].filter((url): url is string => Boolean(url))

    const streetAddress = visibleField('address')
    const hasAddress = Boolean(streetAddress || current.geocity)

    return {
        '@context': 'https://schema.org',
        '@type': ORG_SCHEMA_TYPES[current.type] || 'Organization',
        '@id': `${canonicalUrl.value}#organization`,
        'name': orgName.value,
        'url': canonicalUrl.value,
        ...(logoUrl.value ? { logo: logoUrl.value, image: logoUrl.value } : {}),
        ...(plainDescription.value ? { description: truncate(plainDescription.value, 500) } : {}),
        ...(sameAs.length ? { sameAs: [...new Set(sameAs)] } : {}),
        ...(hasAddress
            ? {
                  address: {
                      '@type': 'PostalAddress',
                      ...(streetAddress ? { streetAddress, postalCode: current.zip || undefined } : {}),
                      ...(current.geocity ? { addressLocality: current.geocity } : {}),
                      'addressCountry': !current.country || current.country === 'Slovensko' ? 'SK' : current.country
                  }
              }
            : {})
    }
})

const breadcrumbJsonLd = computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
        { '@type': 'ListItem', 'position': 1, 'name': 'Domov', 'item': SITE_URL },
        { '@type': 'ListItem', 'position': 2, 'name': 'Organizácie', 'item': `${SITE_URL}/organizacie` },
        { '@type': 'ListItem', 'position': 3, 'name': orgName.value, 'item': canonicalUrl.value }
    ]
}))

// Escape "<" so an org description can never close the <script> tag
function toJsonLd(value: unknown): string {
    return JSON.stringify(value).replace(/</g, '\\u003c')
}

useHead(() => ({
    title: pageTitle.value,
    meta: [
        // Thin profiles (see isThinOrg) stay out of the index but pass on their links
        ...(data.value?.thin ? [{ name: 'robots', content: 'noindex, follow' }] : []),
        { name: 'description', content: metaDescription.value },
        { property: 'og:title', content: pageTitle.value },
        { property: 'og:description', content: metaDescription.value },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: canonicalUrl.value },
        { property: 'og:image', content: ogImage.value },
        { property: 'og:image:alt', content: `Profilový obrázok organizácie/iniciatívy ${orgName.value}` }
    ],
    // `key` dedupes against the site-wide canonical set in app.vue
    link: [{ rel: 'canonical', key: 'canonical', href: canonicalUrl.value }],
    script: organizationJsonLd.value
        ? [
              { key: 'org-jsonld', type: 'application/ld+json', innerHTML: toJsonLd(organizationJsonLd.value) },
              { key: 'org-breadcrumb-jsonld', type: 'application/ld+json', innerHTML: toJsonLd(breadcrumbJsonLd.value) }
          ]
        : []
}))
</script>
